/**
 * FAQ renderer for the Storyblok Visual Editor preview.
 *
 * Mirrors benefits-mobile-app/src/screens/faq/FaqBlock.tsx +
 * QuestionsBlock.tsx — same layout/styling, but in MUI + React DOM
 * since client-portal is web, not React Native.
 *
 * Three entrypoints depending on which story the editor opened:
 *   - <FaqPagePreview>     : story.component === 'content_page'   → full page (categories + items)
 *   - <FaqCategoryPreview> : story.component === 'content_category' → single section
 *   - <FaqQuestionPreview> : story.component === 'faq_question'   → single Q&A
 *
 * Storyblok editor's click-to-edit support: every block is wrapped with
 * `data-blok-c` + `data-blok-uid` attributes via the `storyblokEditable()`
 * helper. That way clicking a question/section inside the iframe focuses
 * the right field in Storyblok's side panel.
 *
 * Theme: defaults to neutral tokens that match the mobile app's fallback
 * (textColor #0D1C3D, muted #4A546A, divider #DADADA, card white +
 * soft shadow). Real per-tenant theming lands in Phase 3.
 */

import React, { useState } from 'react';
import { Box, Stack, Typography, ButtonBase, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { storyblokEditable } from '@storyblok/react';
import { extractHtmlDocument, resolvePreviewHtml, withPreviewDocumentStyles } from './htmlPreview';

// ── Theme tokens (mobile-app fallback colors) ────────────────────────
const COLORS = {
  textColor: '#0D1C3D',
  mutedText: '#4A546A',
  divider: '#DADADA',
  cardBg: '#FFFFFF',
  iconExpand: '#0D1C3D',
  iconCollapse: '#0D1C3D',
};

const CARD_SHADOW = '0px 12px 32px 0px rgba(233, 235, 238, 0.3)';

// ── Types — mirror what comes back from the CDN with resolve_relations ──
interface StoryblokRel {
  _uid?: string;
  _editable?: string;
}

interface FaqQuestionBlok extends StoryblokRel {
  component: 'faq_question';
  question: string | Record<string, unknown>;
  answer: string | Record<string, unknown>;
}

interface ResolvedItem {
  uuid?: string;
  slug?: string;
  content?: FaqQuestionBlok;
}

interface CategoryBlok extends StoryblokRel {
  component: 'content_category';
  // `name` is the content_category schema's display field — what create/rename
  // write and what editors change in the Visual Editor "Name" field. The older
  // `category_name` was written out-of-schema and stays blank on real data, so
  // it's only a legacy fallback here.
  name?: string;
  category_name?: string;
  items?: ResolvedItem[] | string[]; // resolved → objects, unresolved → uuid strings
}

interface ResolvedCategory {
  uuid?: string;
  name?: string; // Storyblok story name — always set, used as fallback for category_name
  content?: CategoryBlok;
}

interface ContentPageBlok extends StoryblokRel {
  component: 'content_page';
  categories?: ResolvedCategory[] | string[];
}

// ── Helpers ───────────────────────────────────────────────────────────

function isResolved<T extends object>(x: unknown): x is T {
  return typeof x === 'object' && x !== null;
}

/**
 * Storyblok's editor injects an `_editable` HTML comment + sends the
 * data attributes via `storyblokEditable()`. Spread the returned object
 * onto the wrapping element so clicks open the right form field.
 */
function editableProps(blok: StoryblokRel | undefined): Record<string, string> {
  if (!blok) return {};
  // @storyblok/react's storyblokEditable returns { 'data-blok-c': '...', 'data-blok-uid': '...' }
  // when _editable is present; empty object otherwise.
  return storyblokEditable(blok as unknown as Parameters<typeof storyblokEditable>[0]) as Record<string, string>;
}

// ── Single question (Item in mobile app) ──────────────────────────────

interface QuestionItemProps {
  blok: FaqQuestionBlok;
  isLast?: boolean;
  defaultExpanded?: boolean;
}

function QuestionItem({ blok, isLast, defaultExpanded }: QuestionItemProps) {
  const [expanded, setExpanded] = useState<boolean>(!!defaultExpanded);

  return (
    <Box
      {...editableProps(blok)}
      sx={{
        py: 3, // 24px vertical padding
        borderBottom: isLast ? 'none' : `1px solid ${COLORS.divider}`,
      }}
    >
      <ButtonBase
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          textAlign: 'left',
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, color: COLORS.textColor, fontSize: 16, fontWeight: 600 }}>
          <MarkdownInline text={blok.question} />
        </Box>
        <Box sx={{ flexShrink: 0, color: expanded ? COLORS.iconCollapse : COLORS.iconExpand }}>
          {expanded ? <RemoveIcon /> : <AddIcon />}
        </Box>
      </ButtonBase>

      {expanded && (
        <Box sx={{ pt: 2, color: COLORS.mutedText, fontSize: 14, lineHeight: 1.6 }}>
          <MarkdownBlock text={blok.answer} />
        </Box>
      )}
    </Box>
  );
}

// ── Rich-text helpers ─────────────────────────────────────────────────

function MarkdownInline({ text }: { text: string | Record<string, unknown> | undefined | null }) {
  if (!text) return null;
  const html = resolvePreviewHtml(text);
  if (!html) return null;
  // Strip outer <p>…</p> so the question flows inline in the row.
  const inner = html.replace(/^\s*<p>([\s\S]*?)<\/p>\s*$/i, '$1');
  return <span dangerouslySetInnerHTML={{ __html: inner }} />;
}

function MarkdownBlock({ text }: { text: string | Record<string, unknown> | undefined | null }) {
  if (!text) return null;
  const htmlDocument = extractHtmlDocument(text);

  if (htmlDocument) {
    return (
      <Box sx={{ width: '100%', height: 360, overflow: 'hidden' }}>
        <iframe
          srcDoc={withPreviewDocumentStyles(htmlDocument)}
          title="FAQ answer"
          sandbox="allow-scripts"
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      </Box>
    );
  }

  const html = resolvePreviewHtml(text);
  if (!html) return null;
  return <Box sx={{ m: 0, '& p': { m: 0, '& + p': { mt: 1.5 } }, '& ul, & ol': { pl: 3, m: 0, '& li': { mb: 0.5 } }, '& a': { color: COLORS.textColor } }} dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * Cohort variant stories share the same base slug but have a --<cohort_code>
 * suffix (e.g. `how-much-can-i-earn--everyone` vs `how-much-can-i-earn--coh-46a78...`).
 * All variants are listed in the category items[], but the mobile app picks
 * one at runtime based on the user's cohort. In the preview we show only the
 * first variant per logical item, deduplicated by base slug.
 * UUID dedup is a secondary guard for the read-modify-write race that can
 * append the same UUID twice.
 */
function dedupeByUuid(itemsField: Array<ResolvedItem | string>): ResolvedItem[] {
  const seenUuids = new Set<string>();
  const seenSlugs = new Set<string>();
  const out: ResolvedItem[] = [];
  for (const it of itemsField) {
    if (!isResolved<ResolvedItem>(it)) continue;
    if (it.uuid && seenUuids.has(it.uuid)) continue;
    if (it.uuid) seenUuids.add(it.uuid);
    // Strip --everyone or --coh-<hash> suffix to get the logical item slug.
    const base = it.slug?.replace(/--(?:everyone|coh-[a-f0-9]+)$/i, '');
    if (base && seenSlugs.has(base)) continue;
    if (base) seenSlugs.add(base);
    out.push(it);
  }
  return out;
}

// ── Category section (one section per category) ───────────────────────

interface CategorySectionProps {
  categoryName: string;
  items: FaqQuestionBlok[];
  categoryBlok?: CategoryBlok;
}

function CategorySection({ categoryName, items, categoryBlok }: CategorySectionProps) {
  return (
    <Box {...editableProps(categoryBlok)}>
      <Typography
        variant="h6"
        component="h2"
        sx={{ color: COLORS.textColor, fontWeight: 700, mb: 3 }}
      >
        {categoryName}
      </Typography>

      {items.length === 0 ? (
        <Box
          sx={{
            background: COLORS.cardBg,
            borderRadius: 4,
            boxShadow: CARD_SHADOW,
            px: { xs: 2, md: 3 },
            py: 4,
            color: COLORS.mutedText,
            textAlign: 'center',
          }}
        >
          No questions in this category yet. Add a <code>faq_question</code> story inside
          this category folder in Storyblok.
        </Box>
      ) : (
        <Box
          sx={{
            background: COLORS.cardBg,
            borderRadius: 4,
            boxShadow: CARD_SHADOW,
            px: { xs: 2, md: 3 },
          }}
        >
          {items.map((q, i) => (
            <QuestionItem key={q._uid || i} blok={q} isLast={i === items.length - 1} />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Page-level preview (entry point for content_page stories) ─────────

interface FaqPagePreviewProps {
  pageBlok: ContentPageBlok;
}

export function FaqPagePreview({ pageBlok }: FaqPagePreviewProps) {
  const categories = (pageBlok.categories ?? []) as Array<ResolvedCategory | string>;

  const resolved: ResolvedCategory[] = categories.filter(
    (c): c is ResolvedCategory => isResolved<ResolvedCategory>(c),
  );
  const unresolvedCount = categories.length - resolved.length;

  return (
    <Stack
      {...editableProps(pageBlok)}
      spacing={5} // 40px between sections
      sx={{ pt: { xs: 3, md: 0 } }}
    >
      {resolved.length === 0 && unresolvedCount === 0 && (
        <Alert severity="info">
          This FAQ page has no categories yet. Add a <code>content_category</code> story
          inside the page folder to start building the FAQ.
        </Alert>
      )}

      {unresolvedCount > 0 && (
        <Alert severity="warning">
          {unresolvedCount} categor{unresolvedCount === 1 ? 'y was' : 'ies were'} returned
          as a UUID but not resolved. Check the fetch params include{' '}
          <code>resolve_relations=content_page.categories,content_category.items</code>.
        </Alert>
      )}

      {resolved.map((cat) => {
        const catContent = cat.content;
        if (!catContent) return null;
        const itemsField = (catContent.items ?? []) as Array<ResolvedItem | string>;
        const items: FaqQuestionBlok[] = dedupeByUuid(itemsField)
          .map((it) => it.content)
          .filter((c): c is FaqQuestionBlok => !!c);

        // Fallback chain: schema `name` field → legacy `category_name` →
        // Storyblok story name → placeholder. `name` is the content_category
        // schema's display field editors change in the Visual Editor; `cat.name`
        // is the story name set at creation — always present.
        const displayName = catContent.name || catContent.category_name || cat.name || '(unnamed category)';

        return (
          <CategorySection
            key={cat.uuid || catContent._uid || displayName}
            categoryName={displayName}
            items={items}
            categoryBlok={catContent}
          />
        );
      })}
    </Stack>
  );
}

// ── Category-level preview (when editor opens a content_category story) ──

interface FaqCategoryPreviewProps {
  categoryBlok: CategoryBlok;
  storyName?: string;
}

export function FaqCategoryPreview({ categoryBlok, storyName }: FaqCategoryPreviewProps) {
  const itemsField = (categoryBlok.items ?? []) as Array<ResolvedItem | string>;
  const items: FaqQuestionBlok[] = dedupeByUuid(itemsField)
    .map((it) => it.content)
    .filter((c): c is FaqQuestionBlok => !!c);

  return (
    <Stack spacing={5} sx={{ pt: { xs: 3, md: 0 } }}>
      <CategorySection
        categoryName={categoryBlok.name || categoryBlok.category_name || storyName || '(unnamed category)'}
        items={items}
        categoryBlok={categoryBlok}
      />
    </Stack>
  );
}

// ── Single-question preview (when editor opens a faq_question story) ───

interface FaqQuestionPreviewProps {
  questionBlok: FaqQuestionBlok;
}

export function FaqQuestionPreview({ questionBlok }: FaqQuestionPreviewProps) {
  return (
    <Stack spacing={5} sx={{ pt: { xs: 3, md: 0 } }}>
      <Box
        sx={{
          background: COLORS.cardBg,
          borderRadius: 4,
          boxShadow: CARD_SHADOW,
          px: { xs: 2, md: 3 },
        }}
      >
        <QuestionItem blok={questionBlok} isLast defaultExpanded />
      </Box>
    </Stack>
  );
}
